import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router'
import DOMPurify from 'dompurify'
import { FaUser, FaRegComment, FaRegHeart, FaHeart } from 'react-icons/fa'
import { LuDot } from 'react-icons/lu'

import {
  useUpdateReplyLikesMutation,
  useDeleteReplyMutation,
} from '../../services/apiSlice'
import { CurrentUser } from '../../services/schema/User'
import { Reply } from '../../services/schema/Post'
import Filler from '../utils/Filler'
import { stringShortener } from '../../services/helpers'

interface Props {
  data: Reply
  canBeDeleted: boolean
}

const ReplyCard: React.FC<Props> = ({ data, canBeDeleted }) => {
  const navigate = useNavigate()
  const user = useOutletContext<CurrentUser>()
  const [updateReplyLikes] = useUpdateReplyLikesMutation()
  const [deleteReply] = useDeleteReplyMutation()
  const [isLiked, setIsLiked] = useState(
    user.likedPosts?.includes(data.replyId),
  )
  const [debouncer, setDebouncer] = useState<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)

  if (data) {
    const profilePic =
      data.replierProfilePicture !== null
        ? data.replierProfilePicture
        : '/user_sample.jpg'

    const usernameShort = stringShortener(data.replierUserName, 8)
    const displayNameShort = stringShortener(data.replierDisplayName, 8)

    const toggleLike = () => {
      const newState = !isLiked

      setIsLiked(newState)
      if (debouncer) {
        clearTimeout(debouncer)
        setDebouncer(undefined)
        return
      }

      const timeout = setTimeout(() => {
        // Place backend API request here...
        updateReplyLikes({
          replyId: data.replyId,
          replierUserName: data.replierUserName,
          postId: data.postId,
          type: `${newState ? 'add' : 'remove'}`,
        })

        setDebouncer(undefined)
      }, 1000)

      setDebouncer(timeout)
    }

    const sanitized = DOMPurify.sanitize(data.postText as string)

    const main = (
      <div className="flex h-auto w-full flex-col">
        <div className="flex w-full justify-between">
          <div className="flex">
            <button
              data-typeid="post-profile-btn"
              onClick={() => {
                navigate(`/${data.replierUserName}`, { replace: true })
              }}
              className="flex cursor-pointer items-center"
            >
              <div className="hover:underline hover:underline-offset-2">
                <p>{displayNameShort}</p>
              </div>
              <span className="text-blue pl-[5px]">
                <FaUser />
              </span>
            </button>
            <button
              data-typeid="post-profile-btn"
              onClick={() => {
                navigate(`/${data.replierUserName}`, { replace: true })
              }}
              className="text-sub cursor-pointer pl-[5px]"
            >
              <div>
                <p>@{usernameShort}</p>
              </div>
            </button>
            <div className="text-sub flex items-center pl-[2px]">
              <LuDot />
            </div>
            <div className="text-sub flex items-center pl-[2px]">
              <p>
                {data.timeline.withinTheYear
                  ? data.timeline.short
                  : data.timeline.long}
              </p>
            </div>
          </div>
          {canBeDeleted && (
            <div>
              <button
                data-typeid="delete-post-btn"
                type="button"
                className="border-theme bgd-theme-wb text-main-invert mr-[5px] cursor-pointer rounded-full border px-[10px] text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                // disabled={true}
                onClick={() => {
                  if (confirm('Are you sure you want to delete this post?')) {
                    deleteReply({
                      replyId: data.replyId,
                      postId: data.postId,
                      posterName: data.posterUserName,
                    })
                  }
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
        <div
          className="w-full pb-[10px]"
          dangerouslySetInnerHTML={{ __html: sanitized as string }}
        />
        <Filler />
        <div className="text-sub flex h-auto w-full justify-evenly pt-[5px] pb-[8px]">
          <div className="text-red flex h-auto w-auto grow items-center opacity-30">
            <button
              data-typeid="post-reply-btn"
              type="button"
              className="relative flex h-[30px] w-auto cursor-not-allowed items-center"
              disabled={true}
            >
              <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full">
                <FaRegComment />
              </div>
              <p>{data.replyCount}</p>
            </button>
          </div>
          <div className="flex h-auto w-auto grow items-center">
            <button
              data-typeid="post-like-btn"
              type="button"
              className="hover-like relative flex h-[30px] w-auto cursor-pointer items-center"
              onClick={() => {
                toggleLike()
              }}
            >
              <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full">
                {isLiked ? <FaHeart className="text-red" /> : <FaRegHeart />}
              </div>
              <p>{data.likeCount}</p>
            </button>
          </div>
          <div className="relative flex h-[30px] w-auto grow items-center"></div>
        </div>
      </div>
    )

    return (
      <section className="border-theme hover-sub flex h-auto w-full grow self-stretch border-b pr-[25px] pl-[15px]">
        <article className="h-auto w-full">
          <div className="flex h-auto pt-[10px]">
            <div className="mr-[8px] h-auto w-[50px] shrink-0">
              <div
                data-id="profile-picture"
                className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-cover bg-center"
                style={{
                  backgroundImage: `url(${profilePic})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            </div>
            {main}
          </div>
        </article>
      </section>
    )
  }
}

export default ReplyCard
